<template>
  <li ref="notificationRoot" class="nav-item notification-wrapper">
    <button
        type="button"
        class="notification-button"
        aria-label="Notifications"
        aria-haspopup="dialog"
        :aria-expanded="String(isOpen)"
        @click.stop="toggleDropdown"
    >
      <span class="notification-button-surface">
        <feather-icon icon="BellIcon" size="20" />
      </span>
      <span v-if="unreadCount > 0" class="notification-badge">
        {{ displayedUnreadCount }}
      </span>
    </button>

    <transition name="notification-transition">
      <section
          v-if="isOpen"
          class="notification-dropdown"
          role="dialog"
          aria-label="Notifications"
          @click.stop
      >
        <header class="notification-header">
          <div class="notification-heading">
            <span class="notification-heading-icon">
              <feather-icon icon="BellIcon" size="18" />
            </span>
            <div class="notification-heading-content">
              <strong class="notification-title">Notifications</strong>
              <span class="notification-subtitle">{{ notificationSummary }}</span>
            </div>
          </div>
          <div class="notification-actions">
            <button
                type="button"
                class="notification-icon-button"
                :title="isMuted ? 'Unmute notifications' : 'Mute notifications'"
                :aria-label="isMuted ? 'Unmute notifications' : 'Mute notifications'"
                @click="toggleMuted"
            >
              <feather-icon :icon="isMuted ? 'VolumeXIcon' : 'Volume2Icon'" size="16" />
            </button>
            <button
                type="button"
                class="notification-icon-button"
                title="Close notifications"
                aria-label="Close notifications"
                @click="closeDropdown"
            >
              <feather-icon icon="XIcon" size="17" />
            </button>
          </div>
        </header>

        <div class="notification-toolbar">
          <div class="socket-status">
            <span class="socket-status-dot" :class="socketStatusClass" />
          </div>
          <button
              type="button"
              class="read-all-button"
              :disabled="isReadAllDisabled"
              @click="markAllAsRead"
          >
            <span v-if="markingAllAsRead" class="button-spinner" />
            <feather-icon v-else icon="CheckCircleIcon" size="14" />
            <span>{{ markingAllAsRead ? 'Marking...' : 'Mark all as read' }}</span>
          </button>
        </div>

        <div ref="notificationList" class="notification-list" @scroll.passive="handleListScroll">
          <div v-if="isInitialLoading" class="notification-state-view">
            <span class="loading-spinner" />
            <strong>Loading notifications</strong>
            <span>Syncing your latest activity...</span>
          </div>
          <div v-else-if="notifications.length === 0" class="notification-state-view">
            <span class="notification-empty-icon">
              <feather-icon icon="BellOffIcon" size="28" />
            </span>
            <strong>No notifications yet</strong>
            <span>New system activity will appear here.</span>
          </div>
          <template v-else>
            <article
                v-for="notification in notifications"
                :key="notification.key"
                class="notification-item"
                :class="{
                'notification-item-unread': !notification.isRead,
                'notification-item-busy': isMarkingNotification(notification),
                'notification-item-selected': isSelected(notification),
              }"
                role="button"
                tabindex="0"
                @click="openNotification(notification)"
                @keydown.enter.prevent="openNotification(notification)"
                @keydown.space.prevent="openNotification(notification)"
            >
              <span class="notification-item-icon">
                <feather-icon :icon="notification.isRead ? 'CheckIcon' : 'BellIcon'" size="15" />
              </span>
              <div class="notification-content">
                <div class="notification-content-header">
                  <strong class="notification-subject">{{ notification.subject }}</strong>
                  <span v-if="!notification.isRead" class="notification-new-label">New</span>
                </div>
                <span class="notification-description">
                  {{ notification.description || 'No additional details.' }}
                </span>
                <div class="notification-date">
                  <span>{{ formatDate(notification.createdAt) }}</span>
                  <span class="notification-date-divider" />
                  <span>{{ formatTime(notification.createdAt) }}</span>
                </div>
              </div>
              <div class="notification-item-action">
                <span v-if="isMarkingNotification(notification)" class="item-spinner" />
                <button
                    v-else-if="!notification.isRead"
                    type="button"
                    class="notification-read-button"
                    title="Mark as read"
                    aria-label="Mark notification as read"
                    @click.stop="markAsRead(notification)"
                >
                  <feather-icon icon="CheckIcon" size="14" />
                </button>
                <feather-icon v-else icon="ChevronRightIcon" size="16" />
              </div>
            </article>

            <div v-if="loadingMore" class="notification-list-message">
              <span class="small-spinner" />
              <span>Loading more notifications...</span>
            </div>
            <div v-else-if="!hasMore" class="notification-list-message">
              All notifications are loaded.
            </div>
          </template>
        </div>

        <transition name="selected-transition">
          <aside v-if="selectedNotification" class="selected-notification">
            <div class="selected-notification-header">
              <span class="selected-notification-icon">
                <feather-icon icon="InfoIcon" size="15" />
              </span>
              <div class="selected-notification-title">
                <strong>{{ selectedNotification.subject }}</strong>
                <small>{{ formatFullDate(selectedNotification.createdAt) }}</small>
              </div>
              <button
                  type="button"
                  class="selected-close-button"
                  title="Close details"
                  aria-label="Close details"
                  @click="selectedNotification = null"
              >
                <feather-icon icon="XIcon" size="14" />
              </button>
            </div>
            <p>{{ selectedNotification.description || 'No additional details.' }}</p>
          </aside>
        </transition>
      </section>
    </transition>
  </li>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import subscriberBus, { NOTIFICATION_BUS_EVENTS } from '@/libs/subscriberBus'
import { getLastUserData } from '@/libs/vue-signalR'
import {
  NotificationGetForWebRequest,
  NotificationMarkAsReadRequest,
  NotificationMarkAllAsReadRequest,
} from '@/libs/Api/Notifications'

const PAGE_SIZE = 10
const LOAD_MORE_THRESHOLD = 80
const REFRESH_DELAY = 350

const SOCKET_STATUS = {
  CONNECTED: 'Connected',
  RECONNECTING: 'Reconnecting',
  DISCONNECTED: 'Disconnected',
}

function getValue(source, keys, fallback) {
  if (!source || typeof source !== 'object') return fallback
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key]
  }
  return fallback
}

function toNumber(value, fallback = null) {
  const num = Number(value)
  return Number.isFinite(num) ? num : fallback
}

function toPositiveInteger(value, fallback = null) {
  const num = toNumber(value)
  return num !== null && num > 0 ? Math.trunc(num) : fallback
}

function toNonNegativeInteger(value, fallback = 0) {
  const num = toNumber(value)
  return num !== null && num >= 0 ? Math.trunc(num) : fallback
}

function toBoolean(value) {
  if (value === true || value === false) return value
  if (value === 1 || value === '1') return true
  if (value === 0 || value === '0') return false
  if (typeof value === 'string') return value.toLowerCase() === 'true'
  return false
}

function toArray(value) {
  if (Array.isArray(value)) return value
  if (value && typeof value === 'object' && Array.isArray(value.$values)) return value.$values
  return []
}

function toTimestamp(value) {
  const ts = new Date(value).getTime()
  return Number.isNaN(ts) ? 0 : ts
}

function getNestedData(response) {
  let current = response
  for (let i = 0; i < 8; i++) {
    if (!current || typeof current !== 'object') return null
    if (current.data && typeof current.data === 'object') current = current.data
    else if (current.Data && typeof current.Data === 'object') current = current.Data
    else if (current.result && typeof current.result === 'object') current = current.result
    else if (current.Result && typeof current.Result === 'object') current = current.Result
    else break
  }
  return current
}

function parseNotificationContainer(response) {
  const container = getNestedData(response)
  if (!container) return null
  const keys = ['unReadNotification', 'ReadNotification', 'UnReadNotification', 'ReadNotification']
  for (const key of keys) {
    if (container[key] !== undefined) return container
  }
  return null
}

function parseUserDataContainer(response) {
  const container = getNestedData(response)
  if (!container) return null
  const keys = ['id', 'Id', 'userId', 'UserId']
  for (const key of keys) {
    if (container[key] !== undefined && container[key] !== null) return container
  }
  return null
}

function parseStoredUserData() {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem('userData')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export default {
  name: 'NotificationDropdown',

  data() {
    return {
      isOpen: false,
      isMuted: false,
      notifications: [],
      selectedNotification: null,
      unreadCount: 0,
      totalCount: 0,
      adminUserId: null,
      currentPage: 0,
      pageSize: PAGE_SIZE,
      hasMore: true,
      loadingFirstPage: false,
      loadingMore: false,
      markingAllAsRead: false,
      markingNotificationKeys: [],
      refreshTimer: null,
      socketStatus: SOCKET_STATUS.DISCONNECTED,
      socketMessage: 'Waiting for notification connection...',
      audio: null,
      audioUnlocked: false,
    }
  },

  computed: {
    displayedUnreadCount() {
      return this.unreadCount > 99 ? '99+' : this.unreadCount
    },
    notificationSummary() {
      if (this.unreadCount === 0) return 'You are all caught up'
      return `${this.unreadCount} unread notification${this.unreadCount === 1 ? '' : 's'}`
    },
    hasAdminUserId() {
      return toPositiveInteger(this.adminUserId) !== null
    },
    isInitialLoading() {
      return this.loadingFirstPage && this.notifications.length === 0
    },
    isReadAllDisabled() {
      return !this.hasAdminUserId || this.unreadCount === 0 || this.markingAllAsRead
    },
    socketStatusClass() {
      if (this.socketStatus === SOCKET_STATUS.CONNECTED) return 'socket-connected'
      if (this.socketStatus === SOCKET_STATUS.RECONNECTING) return 'socket-connecting'
      return 'socket-disconnected'
    },
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleDocumentKeydown)
    this.prepareAudio()
    this.registerSubscriberEvents()
    this.initializeUserData()
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleDocumentKeydown)
    this.removeSubscriberEvents()
    this.clearRefreshTimer()
    this.destroyAudio()
  },

  methods: {
    initializeUserData() {
      const cached = getLastUserData()
      const stored = parseStoredUserData()
      const userData = cached || stored
      if (userData) this.handleUserData(userData)
    },

    registerSubscriberEvents() {
      subscriberBus.$on(NOTIFICATION_BUS_EVENTS.CONNECTED, this.handleConnected)
      subscriberBus.$on(NOTIFICATION_BUS_EVENTS.RECONNECTING, this.handleReconnecting)
      subscriberBus.$on(NOTIFICATION_BUS_EVENTS.DISCONNECTED, this.handleDisconnected)
      subscriberBus.$on(NOTIFICATION_BUS_EVENTS.USER_DATA, this.handleUserData)
      subscriberBus.$on(NOTIFICATION_BUS_EVENTS.USER_NOTIFICATION, this.handleUserNotification)
      subscriberBus.$on(NOTIFICATION_BUS_EVENTS.REFRESH_NOTIFICATIONS, this.handleRefreshRequest)
    },

    removeSubscriberEvents() {
      subscriberBus.$off(NOTIFICATION_BUS_EVENTS.CONNECTED, this.handleConnected)
      subscriberBus.$off(NOTIFICATION_BUS_EVENTS.RECONNECTING, this.handleReconnecting)
      subscriberBus.$off(NOTIFICATION_BUS_EVENTS.DISCONNECTED, this.handleDisconnected)
      subscriberBus.$off(NOTIFICATION_BUS_EVENTS.USER_DATA, this.handleUserData)
      subscriberBus.$off(NOTIFICATION_BUS_EVENTS.USER_NOTIFICATION, this.handleUserNotification)
      subscriberBus.$off(NOTIFICATION_BUS_EVENTS.REFRESH_NOTIFICATIONS, this.handleRefreshRequest)
    },

    handleConnected(payload) {
      this.socketStatus = SOCKET_STATUS.CONNECTED
      this.socketMessage = payload?.isReconnected ? 'Connection restored.' : 'Connected to notification server.'
      if (this.hasAdminUserId) this.scheduleRefresh(200)
    },

    handleReconnecting() {
      this.socketStatus = SOCKET_STATUS.RECONNECTING
      this.socketMessage = 'Reconnecting to notification server...'
    },

    handleDisconnected() {
      this.socketStatus = SOCKET_STATUS.DISCONNECTED
      this.socketMessage = 'Notification server is disconnected.'
    },

    handleUserData(response) {
      const userData = parseUserDataContainer(response)
      if (!userData) return
      const userId = toPositiveInteger(getValue(userData, ['id', 'Id', 'userId', 'UserId'], null))
      if (userId === null) return
      const previousId = this.adminUserId
      this.adminUserId = userId
      const unread = getValue(userData, ['unReadTotalCount', 'UnReadTotalCount'], undefined)
      if (unread !== undefined) this.unreadCount = toNonNegativeInteger(unread, this.unreadCount)
      if (previousId !== userId) this.resetNotifications()
      if (previousId !== userId || this.notifications.length === 0) {
        this.scheduleRefresh(150)
      }
    },

    async handleUserNotification(response) {
      const source = getNestedData(response)
      if (!source || (!source.subject && !source.Subject && !source.description && !source.Description)) {
        this.socketMessage = 'A new notification was received.'
        this.scheduleRefresh()
        return
      }
      const notification = this.normalizeNotification(source, false)
      const inserted = this.upsertLiveNotification(notification)
      if (inserted) {
        this.unreadCount += 1
        this.totalCount += 1
        this.socketMessage = `New notification: ${notification.subject}`
        this.showNotificationToast(notification)
        await this.playNotificationSound()
      }
      this.scheduleRefresh()
    },

    handleRefreshRequest() {
      this.scheduleRefresh()
    },

    scheduleRefresh(delay = REFRESH_DELAY) {
      this.clearRefreshTimer()
      this.refreshTimer = window.setTimeout(() => {
        this.refreshTimer = null
        this.loadFirstPage()
      }, delay)
    },

    clearRefreshTimer() {
      if (this.refreshTimer) {
        window.clearTimeout(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    resetNotifications() {
      this.notifications = []
      this.selectedNotification = null
      this.currentPage = 0
      this.totalCount = 0
      this.hasMore = true
      this.markingNotificationKeys = []
    },

    async loadFirstPage() {
      return this.loadNotifications(1)
    },

    async loadNextPage() {
      if (!this.hasMore || this.loadingFirstPage || this.loadingMore) return
      return this.loadNotifications(this.currentPage + 1)
    },

    async loadNotifications(pageNumber) {
      if (!this.hasAdminUserId) return
      const isFirst = pageNumber === 1
      if ((isFirst && this.loadingFirstPage) || (!isFirst && this.loadingMore)) return
      if (isFirst) this.loadingFirstPage = true
      else this.loadingMore = true

      try {
        const request = new NotificationGetForWebRequest(this)
        request.setParams({
          userId: this.adminUserId,
          pageNumber,
          count: this.pageSize,
        })
        const response = await request.fetch()
        this.checkResponse(response)
        const page = this.parsePage(response)
        this.notifications = isFirst
            ? this.mergeCollections([], page.items)
            : this.mergeCollections(this.notifications, page.items)
        this.currentPage = pageNumber
        this.unreadCount = page.unreadCount
        this.totalCount = page.totalCount
        this.hasMore = page.hasMore(this.notifications.length, this.pageSize)
        this.syncSelectedNotification()
        if (this.socketStatus === SOCKET_STATUS.CONNECTED) {
          this.socketMessage = this.hasMore ? 'Notifications are up to date.' : 'All notifications are loaded.'
        }
      } catch (error) {
        console.error('[NotificationDropdown] Load failed:', error)
        this.socketMessage = isFirst ? 'Could not load notifications.' : 'Could not load more notifications.'
        this.showErrorToast(isFirst ? 'Could not load notifications' : 'Could not load more notifications', error)
      } finally {
        if (isFirst) this.loadingFirstPage = false
        else this.loadingMore = false
      }
    },

    parsePage(response) {
      const container = parseNotificationContainer(response)
      if (!container) throw new Error('Notification container not found')
      const unreadItems = toArray(getValue(container, ['unReadNotification', 'UnReadNotification'], [])).map(item =>
          this.normalizeNotification(item, false)
      )
      const readItems = toArray(getValue(container, ['ReadNotification', 'readNotification'], [])).map(item =>
          this.normalizeNotification(item, true)
      )
      const items = this.sortNotifications([...unreadItems, ...readItems])
      const unreadCount = toNonNegativeInteger(
          getValue(container, ['unReadTotalCount', 'UnReadTotalCount'], unreadItems.length),
          unreadItems.length
      )
      const readCount = toNonNegativeInteger(
          getValue(container, ['readTotalCount', 'ReadTotalCount'], readItems.length),
          readItems.length
      )
      const hasServerCounts = getValue(container, ['unReadTotalCount', 'UnReadTotalCount'], undefined) !== undefined
      const totalCount = hasServerCounts ? unreadCount + readCount : items.length
      return {
        items,
        unreadCount,
        totalCount,
        hasMore: (loaded, size) => (hasServerCounts ? loaded < totalCount : items.length >= size),
      }
    },

    normalizeNotification(source, forcedRead) {
      const id = getValue(source, ['id', 'Id', 'notificationId', 'NotificationId', 'systemNotificationId', 'SystemNotificationId'], null)
      const createdAt = getValue(source, ['createdAt', 'CreatedAt', 'createDate', 'CreateDate', 'createdDate', 'CreatedDate', 'date', 'Date'], new Date().toISOString())
      const subject = getValue(source, ['subject', 'Subject', 'title', 'Title'], 'New notification')
      const description = getValue(source, ['description', 'Description', 'message', 'Message'], '')
      const isRead = typeof forcedRead === 'boolean' ? forcedRead : toBoolean(getValue(source, ['isRead', 'IsRead', 'read', 'Read'], false))
      const key = id !== null ? String(id) : [createdAt, subject, description].join('|')
      return { id, key, createdAt, subject: String(subject), description: String(description), isRead }
    },

    mergeCollections(current, incoming) {
      const map = new Map()
      current.forEach(n => map.set(n.key, n))
      incoming.forEach(n => {
        const existing = map.get(n.key)
        map.set(n.key, existing ? { ...existing, ...n } : n)
      })
      return this.sortNotifications(Array.from(map.values()))
    },

    sortNotifications(items) {
      return items.sort((a, b) => toTimestamp(b.createdAt) - toTimestamp(a.createdAt))
    },

    upsertLiveNotification(notification) {
      const index = this.notifications.findIndex(n => n.key === notification.key)
      if (index === -1) {
        this.notifications = this.sortNotifications([notification, ...this.notifications])
        return true
      }
      this.$set(this.notifications, index, { ...this.notifications[index], ...notification })
      this.notifications = this.sortNotifications([...this.notifications])
      return false
    },

    async openNotification(notification) {
      this.selectedNotification = { ...notification }
      if (!notification.isRead) await this.markAsRead(notification)
    },

    async markAsRead(notification) {
      if (!notification || notification.isRead || !this.hasAdminUserId || this.isMarkingNotification(notification)) return
      const id = toPositiveInteger(notification.id)
      if (id === null) {
        this.showErrorToast('Invalid notification ID', new Error('ID missing'))
        return
      }
      this.addMarkingNotification(notification)
      try {
        const request = new NotificationMarkAsReadRequest(this)
        request.setPayload({ userId: this.adminUserId, notificationId: id })
        const response = await request.fetch()
        this.checkResponse(response)
        this.updateNotificationReadState(notification.key, true)
        this.unreadCount = Math.max(0, this.unreadCount - 1)
      } catch (error) {
        console.error('[NotificationDropdown] Mark as read failed:', error)
        this.showErrorToast('Notification was not marked as read', error)
      } finally {
        this.removeMarkingNotification(notification)
      }
    },

    async markAllAsRead() {
      if (this.isReadAllDisabled) return
      this.markingAllAsRead = true
      try {
        const request = new NotificationMarkAllAsReadRequest(this)
        request.setPayload(this.adminUserId)
        const response = await request.fetch()
        this.checkResponse(response)
        this.notifications = this.notifications.map(n => ({ ...n, isRead: true }))
        if (this.selectedNotification) this.selectedNotification = { ...this.selectedNotification, isRead: true }
        this.unreadCount = 0
        this.showSuccessToast('All notifications marked as read.')
      } catch (error) {
        console.error('[NotificationDropdown] Mark all as read failed:', error)
        this.showErrorToast('Notifications were not marked as read', error)
      } finally {
        this.markingAllAsRead = false
      }
    },

    updateNotificationReadState(key, isRead) {
      const idx = this.notifications.findIndex(n => n.key === key)
      if (idx !== -1) this.$set(this.notifications, idx, { ...this.notifications[idx], isRead })
      if (this.selectedNotification?.key === key) {
        this.selectedNotification = { ...this.selectedNotification, isRead }
      }
    },

    addMarkingNotification(notification) {
      if (!this.markingNotificationKeys.includes(notification.key)) {
        this.markingNotificationKeys.push(notification.key)
      }
    },

    removeMarkingNotification(notification) {
      this.markingNotificationKeys = this.markingNotificationKeys.filter(k => k !== notification.key)
    },

    isMarkingNotification(notification) {
      return this.markingNotificationKeys.includes(notification.key)
    },

    isSelected(notification) {
      return !!this.selectedNotification && this.selectedNotification.key === notification.key
    },

    syncSelectedNotification() {
      if (!this.selectedNotification) return
      const updated = this.notifications.find(n => n.key === this.selectedNotification.key)
      this.selectedNotification = updated ? { ...updated } : null
    },

    handleListScroll(event) {
      const list = event.target || this.$refs.notificationList
      if (!list) return
      const remaining = list.scrollHeight - list.scrollTop - list.clientHeight
      if (remaining <= LOAD_MORE_THRESHOLD) this.loadNextPage()
    },

    checkResponse(response) {
      if (!response) throw new Error('No response')
      const src = response.data || response
      const success = getValue(src, ['isSuccess', 'IsSuccess', 'success', 'Success'], true)
      if (!success) {
        const msg = getValue(src, ['message', 'Message', 'error', 'Error'], 'Request failed')
        throw new Error(msg)
      }
    },

    async toggleDropdown() {
      this.isOpen = !this.isOpen
      if (!this.isOpen) {
        this.selectedNotification = null
        return
      }
      await this.unlockAudio()
      if (this.hasAdminUserId) await this.loadFirstPage()
    },

    closeDropdown() {
      this.isOpen = false
      this.selectedNotification = null
    },

    handleOutsideClick(event) {
      const root = this.$refs.notificationRoot
      if (root && !root.contains(event.target)) this.closeDropdown()
    },

    handleDocumentKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) this.closeDropdown()
    },

    prepareAudio() {
      if (typeof window === 'undefined' || typeof Audio === 'undefined') return
      this.audio = new Audio('/sound.mp3')
      this.audio.preload = 'auto'
      this.audio.muted = this.isMuted
    },

    destroyAudio() {
      if (this.audio) {
        this.audio.pause()
        this.audio.src = ''
        this.audio = null
      }
    },

    async unlockAudio() {
      if (!this.audio || this.audioUnlocked) return
      try {
        this.audio.muted = true
        this.audio.currentTime = 0
        await this.audio.play()
        this.audio.pause()
        this.audio.currentTime = 0
        this.audio.muted = this.isMuted
        this.audioUnlocked = true
      } catch {
        this.audio.muted = this.isMuted
      }
    },

    async playNotificationSound() {
      if (!this.audio || this.isMuted) return
      try {
        this.audio.currentTime = 0
        this.audio.muted = false
        await this.audio.play()
      } catch (error) {
        console.warn('[NotificationDropdown] Audio playback blocked:', error)
      }
    },

    toggleMuted() {
      this.isMuted = !this.isMuted
      if (this.audio) this.audio.muted = this.isMuted
    },

    showNotificationToast(notification) {
      this.showToast({
        title: notification.subject,
        text: notification.description || notification.subject,
        icon: 'BellIcon',
        variant: 'success',
      })
    },

    showSuccessToast(message) {
      this.showToast({ title: 'Done', text: message, icon: 'CheckIcon', variant: 'success' })
    },

    showErrorToast(title, error) {
      this.showToast({
        title: 'Error',
        text: error?.message || title,
        icon: 'AlertCircleIcon',
        variant: 'danger',
      })
    },

    showToast({ title, text, icon, variant }) {
      if (typeof this.$toast !== 'function') return
      this.$toast({
        component: ToastificationContent,
        position: 'bottom-center',
        props: { title, text, icon, variant },
      })
    },

    formatDate(value) {
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
    },

    formatTime(value) {
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    },

    formatFullDate(value) {
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.notification-wrapper {
  --notification-primary: #7367f0;
  --notification-primary-strong: #5e50ee;
  --notification-primary-soft: rgba(115, 103, 240, 0.11);
  --notification-danger: #ea5455;
  --notification-success: #28c76f;
  --notification-warning: #ff9f43;
  --notification-surface: #ffffff;
  --notification-surface-soft: #f7f7fb;
  --notification-surface-hover: #f5f3ff;
  --notification-border: rgba(75, 70, 92, 0.12);
  --notification-border-strong: rgba(115, 103, 240, 0.32);
  --notification-text: #4b465c;
  --notification-text-strong: #2f2b3d;
  --notification-text-muted: #8c8799;
  --notification-text-soft: #aaa6b5;
  --notification-shadow: 0 24px 70px rgba(47, 43, 61, 0.18);
  position: relative;
  list-style: none;
  color-scheme: light;
}

:global(.dark) .notification-wrapper {
  --notification-surface: #25293c;
  --notification-surface-soft: #2f3349;
  --notification-surface-hover: #35394f;
  --notification-border: rgba(255, 255, 255, 0.08);
  --notification-border-strong: rgba(115, 103, 240, 0.46);
  --notification-text: #d7d5df;
  --notification-text-strong: #f5f4f8;
  --notification-text-muted: #aaa7b5;
  --notification-text-soft: #807c8d;
  --notification-shadow: 0 28px 80px rgba(10, 12, 20, 0.5);
  color-scheme: dark;
}

.notification-button {
  position: relative;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 0;
  border-radius: 14px;
  color: var(--notification-text);
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.notification-button:hover {
  transform: translateY(-1px);
}

.notification-button:focus-visible {
  outline: none;
}

.notification-button-surface {
  width: 38px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
      color 0.2s ease,
      border-color 0.2s ease,
      background-color 0.2s ease,
      box-shadow 0.2s ease;
}

.notification-button:hover .notification-button-surface,
.notification-button:focus-visible .notification-button-surface {
  color: var(--notification-primary);
  border-color: rgba(115, 103, 240, 0.2);
  background: var(--notification-primary-soft);
  box-shadow: 0 8px 20px rgba(115, 103, 240, 0.12);
}

.notification-badge {
  position: absolute;
  top: -1px;
  right: -3px;
  z-index: 2;
  min-width: 19px;
  height: 19px;
  padding: 0 4px;
  border: 2px solid var(--notification-surface);
  border-radius: 999px;
  color: #ffffff;
  background: linear-gradient(135deg, #ff6b6b, var(--notification-danger));
  box-shadow: 0 6px 16px rgba(234, 84, 85, 0.35);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: notification-badge-in 0.24s ease;
}

.notification-dropdown {
  position: absolute;
  z-index: 1100;
  top: calc(100% + 14px);
  right: -34px;
  width: 420px;
  max-width: calc(100vw - 24px);
  max-height: min(690px, calc(100vh - 96px));
  overflow: hidden;
  border: 1px solid var(--notification-border);
  border-radius: 20px;
  color: var(--notification-text);
  background: var(--notification-surface);
  box-shadow: var(--notification-shadow);
  display: flex;
  flex-direction: column;
  isolation: isolate;
}

.notification-dropdown::before {
  content: "";
  position: absolute;
  top: -7px;
  right: 51px;
  z-index: -1;
  width: 14px;
  height: 14px;
  border-top: 1px solid var(--notification-border);
  border-left: 1px solid var(--notification-border);
  background: var(--notification-surface);
  transform: rotate(45deg);
}

.notification-header {
  flex-shrink: 0;
  padding: 18px 18px 14px;
  border-bottom: 1px solid var(--notification-border);
  background:
      linear-gradient(135deg, rgba(115, 103, 240, 0.08), transparent 58%),
      var(--notification-surface);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.notification-heading {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 11px;
}

.notification-heading-icon {
  flex-shrink: 0;
  width: 39px;
  height: 39px;
  border: 1px solid rgba(115, 103, 240, 0.17);
  border-radius: 12px;
  color: var(--notification-primary);
  background: var(--notification-primary-soft);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-heading-content {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.notification-title {
  color: var(--notification-text-strong);
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.2px;
}

.notification-subtitle {
  margin-top: 3px;
  overflow: hidden;
  color: var(--notification-text-muted);
  font-size: 11px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 7px;
}

.notification-icon-button,
.selected-close-button {
  padding: 0;
  border: 1px solid var(--notification-border);
  color: var(--notification-text-muted);
  background: var(--notification-surface-soft);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
      color 0.2s ease,
      border-color 0.2s ease,
      background-color 0.2s ease,
      transform 0.2s ease;
}

.notification-icon-button {
  width: 34px;
  height: 34px;
  border-radius: 10px;
}

.notification-icon-button:hover,
.selected-close-button:hover {
  color: #ffffff;
  border-color: var(--notification-primary);
  background: var(--notification-primary);
  transform: translateY(-1px);
}

.notification-toolbar {
  flex-shrink: 0;
  margin: 14px 14px 10px;
  padding: 11px 12px;
  border: 1px solid var(--notification-border);
  border-radius: 14px;
  background: var(--notification-surface-soft);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.socket-status {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
}

.socket-status-dot {
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.socket-status-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.socket-status-text strong {
  color: var(--notification-text-strong);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.3;
}

.socket-status-text span {
  max-width: 180px;
  overflow: hidden;
  color: var(--notification-text-muted);
  font-size: 9px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.socket-connected {
  background: var(--notification-success);
  box-shadow: 0 0 0 4px rgba(40, 199, 111, 0.13);
}

.socket-connecting {
  background: var(--notification-warning);
  box-shadow: 0 0 0 4px rgba(255, 159, 67, 0.13);
  animation: notification-pulse 1.2s infinite;
}

.socket-disconnected {
  background: var(--notification-danger);
  box-shadow: 0 0 0 4px rgba(234, 84, 85, 0.13);
}

.read-all-button {
  flex-shrink: 0;
  min-height: 34px;
  padding: 7px 11px;
  border: 1px solid rgba(115, 103, 240, 0.26);
  border-radius: 10px;
  color: var(--notification-primary);
  background: var(--notification-primary-soft);
  cursor: pointer;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition:
      color 0.2s ease,
      border-color 0.2s ease,
      background-color 0.2s ease,
      transform 0.2s ease;
}

.read-all-button:hover:not(:disabled) {
  color: #ffffff;
  border-color: var(--notification-primary);
  background: var(--notification-primary);
  transform: translateY(-1px);
}

.read-all-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.notification-list {
  min-height: 190px;
  max-height: 405px;
  padding: 0 12px 13px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: var(--notification-text-soft) transparent;
  scrollbar-width: thin;
}

.notification-list::-webkit-scrollbar,
.selected-notification p::-webkit-scrollbar {
  width: 5px;
}

.notification-list::-webkit-scrollbar-track,
.selected-notification p::-webkit-scrollbar-track {
  background: transparent;
}

.notification-list::-webkit-scrollbar-thumb,
.selected-notification p::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: var(--notification-text-soft);
}

.notification-item {
  position: relative;
  width: 100%;
  min-height: 82px;
  margin-bottom: 8px;
  padding: 12px;
  border: 1px solid var(--notification-border);
  border-radius: 14px;
  color: var(--notification-text);
  background: var(--notification-surface);
  cursor: pointer;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) 30px;
  align-items: center;
  gap: 10px;
  transition:
      border-color 0.2s ease,
      background-color 0.2s ease,
      box-shadow 0.2s ease,
      transform 0.2s ease;
}

.notification-item:last-of-type {
  margin-bottom: 0;
}

.notification-item:hover,
.notification-item-selected {
  border-color: var(--notification-border-strong);
  background: var(--notification-surface-hover);
  box-shadow: 0 8px 20px rgba(115, 103, 240, 0.08);
  transform: translateY(-1px);
}

.notification-item-unread {
  border-color: rgba(115, 103, 240, 0.22);
  background:
      linear-gradient(90deg, rgba(115, 103, 240, 0.08), transparent 60%),
      var(--notification-surface);
}

.notification-item-unread::before {
  content: "";
  position: absolute;
  top: 12px;
  bottom: 12px;
  left: 0;
  width: 3px;
  border-radius: 0 999px 999px 0;
  background: var(--notification-primary);
}

.notification-item-busy {
  pointer-events: none;
  opacity: 0.72;
}

.notification-item-icon {
  width: 38px;
  height: 38px;
  border: 1px solid var(--notification-border);
  border-radius: 12px;
  color: var(--notification-text-muted);
  background: var(--notification-surface-soft);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-item-unread .notification-item-icon {
  color: var(--notification-primary);
  border-color: rgba(115, 103, 240, 0.18);
  background: var(--notification-primary-soft);
}

.notification-content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.notification-content-header {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 7px;
}

.notification-subject {
  min-width: 0;
  overflow: hidden;
  color: var(--notification-text-strong);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-new-label {
  flex-shrink: 0;
  padding: 3px 6px;
  border-radius: 999px;
  color: var(--notification-primary);
  background: var(--notification-primary-soft);
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.35px;
}

.notification-description {
  overflow: hidden;
  color: var(--notification-text-muted);
  font-size: 10px;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-date {
  margin-top: 1px;
  color: var(--notification-text-soft);
  font-size: 9px;
  line-height: 1.35;
  display: flex;
  align-items: center;
  gap: 6px;
}

.notification-date-divider {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
}

.notification-item-action {
  min-height: 30px;
  color: var(--notification-text-soft);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-read-button {
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid var(--notification-border);
  border-radius: 9px;
  color: var(--notification-primary);
  background: var(--notification-surface-soft);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
      color 0.2s ease,
      border-color 0.2s ease,
      background-color 0.2s ease,
      transform 0.2s ease;
}

.notification-read-button:hover {
  color: #ffffff;
  border-color: var(--notification-primary);
  background: var(--notification-primary);
  transform: translateY(-1px);
}

.notification-state-view {
  min-height: 250px;
  padding: 28px 22px;
  color: var(--notification-text-muted);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.notification-state-view strong {
  color: var(--notification-text-strong);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
}

.notification-state-view > span:last-child {
  margin-top: 5px;
  font-size: 10px;
  line-height: 1.5;
}

.notification-empty-icon {
  width: 68px;
  height: 68px;
  margin-bottom: 15px;
  border: 1px solid rgba(115, 103, 240, 0.17);
  border-radius: 20px;
  color: var(--notification-primary);
  background: var(--notification-primary-soft);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-list-message {
  padding: 12px 8px 2px;
  color: var(--notification-text-muted);
  font-size: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  margin-bottom: 13px;
  border: 3px solid rgba(115, 103, 240, 0.14);
  border-top-color: var(--notification-primary);
  border-radius: 50%;
  animation: notification-spin 0.75s linear infinite;
}

.button-spinner,
.item-spinner,
.small-spinner {
  border: 2px solid rgba(115, 103, 240, 0.18);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: notification-spin 0.7s linear infinite;
}

.button-spinner {
  width: 12px;
  height: 12px;
}

.item-spinner {
  width: 13px;
  height: 13px;
  color: var(--notification-primary);
}

.small-spinner {
  width: 11px;
  height: 11px;
  color: var(--notification-primary);
}

.selected-notification {
  flex-shrink: 0;
  margin: 0 12px 12px;
  padding: 13px;
  border: 1px solid var(--notification-border-strong);
  border-radius: 14px;
  background:
      linear-gradient(135deg, rgba(115, 103, 240, 0.08), transparent 70%),
      var(--notification-surface-soft);
}

.selected-notification-header {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) 28px;
  align-items: start;
  gap: 9px;
}

.selected-notification-icon {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  color: var(--notification-primary);
  background: var(--notification-primary-soft);
  display: flex;
  align-items: center;
  justify-content: center;
}

.selected-notification-title {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.selected-notification-title strong {
  overflow: hidden;
  color: var(--notification-text-strong);
  font-size: 12px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-notification-title small {
  color: var(--notification-text-soft);
  font-size: 8px;
  line-height: 1.4;
}

.selected-notification p {
  max-height: 90px;
  margin: 10px 0 0 39px;
  overflow-y: auto;
  color: var(--notification-text-muted);
  font-size: 10px;
  line-height: 1.75;
  overflow-wrap: anywhere;
}

.selected-close-button {
  width: 28px;
  height: 28px;
  border-radius: 9px;
}

.notification-icon-button:focus-visible,
.selected-close-button:focus-visible,
.read-all-button:focus-visible,
.notification-read-button:focus-visible,
.notification-item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.18);
}

.notification-transition-enter-active,
.notification-transition-leave-active {
  transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  transform-origin: top right;
}

.notification-transition-enter,
.notification-transition-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}

.selected-transition-enter-active,
.selected-transition-leave-active {
  transition:
      opacity 0.2s ease,
      transform 0.2s ease;
}

.selected-transition-enter,
.selected-transition-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@keyframes notification-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes notification-pulse {
  50% {
    opacity: 0.42;
  }
}

@keyframes notification-badge-in {
  from {
    opacity: 0;
    transform: scale(0.68);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 575.98px) {
  .notification-dropdown {
    position: fixed;
    top: 70px;
    right: 8px;
    bottom: 8px;
    left: 8px;
    width: auto;
    max-width: none;
    max-height: none;
    border-radius: 18px;
  }
  .notification-dropdown::before {
    display: none;
  }
  .notification-header {
    padding: 15px 14px 13px;
  }
  .notification-heading {
    gap: 9px;
  }
  .notification-heading-icon {
    width: 36px;
    height: 36px;
    border-radius: 11px;
  }
  .notification-title {
    font-size: 15px;
  }
  .notification-subtitle {
    max-width: 180px;
    font-size: 10px;
  }
  .notification-icon-button {
    width: 32px;
    height: 32px;
  }
  .notification-toolbar {
    margin: 12px 10px 10px;
    padding: 10px;
  }
  .socket-status-text span {
    max-width: 130px;
  }
  .read-all-button {
    padding: 7px 9px;
    font-size: 9px;
  }
  .notification-list {
    flex: 1;
    max-height: none;
    padding-right: 8px;
    padding-left: 8px;
  }
  .notification-item {
    min-height: 78px;
    padding: 10px;
    grid-template-columns: 35px minmax(0, 1fr) 30px;
    gap: 9px;
  }
  .notification-item-icon {
    width: 35px;
    height: 35px;
    border-radius: 11px;
  }
  .notification-subject {
    font-size: 11px;
  }
  .selected-notification {
    margin-right: 8px;
    margin-bottom: 8px;
    margin-left: 8px;
  }
  .selected-notification p {
    margin-left: 0;
  }
}

@media (max-width: 359.98px) {
  .notification-subtitle {
    max-width: 130px;
  }
  .socket-status-text span {
    display: none;
  }
  .read-all-button span:last-child {
    display: none;
  }
  .read-all-button {
    width: 34px;
    padding: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .notification-button,
  .notification-button-surface,
  .notification-icon-button,
  .selected-close-button,
  .read-all-button,
  .notification-read-button,
  .notification-item,
  .notification-transition-enter-active,
  .notification-transition-leave-active,
  .selected-transition-enter-active,
  .selected-transition-leave-active {
    transition: none;
  }
  .socket-connecting,
  .notification-badge,
  .loading-spinner,
  .button-spinner,
  .item-spinner,
  .small-spinner {
    animation-duration: 1.5s;
  }
}
</style>